import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DatepickerComponent } from '@ppl/ui';
import { NgZorroModule } from '../../modules/ng-zorro/ng-zorro.module';
import { PROVIDER_CLASSIFICATION_TYPES } from '../../constants/static-data.constants';

/** Control names the question flow reads and writes on the host's form group. */
export const PROVIDER_CLASSIFICATION_CONTROLS = [
  'servesAll',
  'vendored',
  'followsProgramDesign',
  'vendorNumber',
];

/** Adds the question flow's controls to an existing form group, e.g. a wizard step's. */
export function addProviderClassificationControls(form: FormGroup, fb: FormBuilder): void {
  PROVIDER_CLASSIFICATION_CONTROLS.forEach((name) => {
    if (!form.get(name)) {
      form.addControl(name, fb.control(null));
    }
  });
}

/**
 * Provider Type derived from the answers. Empty until the branch the provider is on
 * has been answered through to the end.
 */
export function deriveProviderType(values: any): string {
  if (!values?.servesAll) {
    return '';
  }

  if (values.servesAll === 'Yes') {
    return PROVIDER_CLASSIFICATION_TYPES.localBusiness;
  }

  if (!values.vendored) {
    return '';
  }

  if (values.vendored === 'No') {
    return PROVIDER_CLASSIFICATION_TYPES.sdpProvider;
  }

  if (!values.followsProgramDesign) {
    return '';
  }

  return values.followsProgramDesign === 'Yes'
    ? PROVIDER_CLASSIFICATION_TYPES.regionalCenterVendor
    : PROVIDER_CLASSIFICATION_TYPES.sdpProvider;
}

/**
 * The Provider Classification question flow — Q1, its conditional follow-ups and the
 * derived read-only Provider Type. Presentational: the host owns the form group and
 * whatever persistence it needs, so the referral wizard and the provider detail page
 * can share one implementation.
 */
@Component({
  selector: 'ppl-provider-classification-questions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, NgZorroModule, DatepickerComponent],
  templateUrl: './provider-classification-questions.component.html',
  styleUrl: './provider-classification-questions.component.css',
})
export class ProviderClassificationQuestionsComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() mode: 'read' | 'edit' = 'edit';
  /** Label column width, so the questions line up with the host form's other fields. */
  @Input() labelSpan = 8;

  /**
   * Signature attestation. Owned here rather than added to the host's form group: it is a
   * save-time gate, not a saved answer, and both hosts serialize their whole form group.
   * Confirmed again on every save, so nothing is read back for it either.
   */
  readonly attestation = new FormControl(false, { nonNullable: true });

  private readonly destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    // Answering an earlier question invalidates whatever branch came after it.
    // Initial Filing Date is asked of everyone, so it is never cleared.
    this.form.get('servesAll')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.clearBranch(['vendored', 'followsProgramDesign', 'vendorNumber']));

    this.form.get('vendored')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.clearBranch(['followsProgramDesign', 'vendorNumber']));

    // The vendor number only belongs to a Regional Center Vendor.
    this.form.get('followsProgramDesign')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.clearBranch(['vendorNumber']));

    this.applyBranchValidators();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get servesAll() {
    return this.form?.get('servesAll')?.value;
  }

  get vendored() {
    return this.form?.get('vendored')?.value;
  }

  get followsProgramDesign() {
    return this.form?.get('followsProgramDesign')?.value;
  }

  /** Q2 is only asked when the provider serves only individuals with I/DD. */
  get showVendored() {
    return this.servesAll === 'No';
  }

  /** Q3 is only asked of a provider vendored with a regional center. */
  get showProgramDesignQuestion() {
    return this.showVendored && this.vendored === 'Yes';
  }

  /** The vendor details only apply once the answers resolve to Regional Center Vendor. */
  get showVendorDetails() {
    return this.derivedProviderType === PROVIDER_CLASSIFICATION_TYPES.regionalCenterVendor;
  }

  get derivedProviderType(): string {
    return deriveProviderType(this.form?.value);
  }

  control(name: string) {
    return this.form?.get(name);
  }

  /** The attestation error only surfaces once the host has asked for confirmation. */
  get showAttestationError(): boolean {
    return this.attestation.touched && !this.attestation.value;
  }

  /**
   * Save-time gate: true when the attestation has been confirmed. Hosts call this instead
   * of reading the control so an unconfirmed attestation also shows its error. A disabled
   * form is a read-only wizard step with nothing to save, so there is nothing to attest to.
   */
  confirmAttestation(): boolean {
    if (this.form?.disabled || this.attestation.value) {
      return true;
    }

    this.attestation.markAsTouched();
    this.cdr.markForCheck();
    return false;
  }

  /** Clears the attestation so it is confirmed afresh for the next set of answers. */
  resetAttestation(): void {
    this.attestation.setValue(false);
    this.attestation.markAsUntouched();
    this.cdr.markForCheck();
  }

  showError(name: string): boolean {
    const control = this.control(name);
    return !!(control?.invalid && control?.touched);
  }

  /**
   * Required only applies to the questions currently on screen. Call this after
   * patching answers in silently, since no valueChanges fires for those.
   *
   * Also marks this view for check: the host owns the form group, so a silent patch
   * changes no input binding and OnPush would otherwise leave the answers unrendered.
   */
  applyBranchValidators(): void {
    this.setRequired('servesAll', true);
    this.setRequired('vendored', this.showVendored);
    this.setRequired('followsProgramDesign', this.showProgramDesignQuestion);
    this.setRequired('vendorNumber', this.showVendorDetails);
    this.cdr.markForCheck();
  }

  private clearBranch(names: string[]): void {
    const cleared = names.reduce((values: any, name: string) => ({ ...values, [name]: null }), {});
    this.form.patchValue(cleared, { emitEvent: false });
    this.applyBranchValidators();
  }

  private setRequired(name: string, required: boolean): void {
    const control = this.control(name);
    if (!control) {
      return;
    }

    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }
}
